import React, { memo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { RouterActions, StyledHeading, StyledTextRoofPage } from "@/components";
import { StyledQuestion } from "@/components/StyledQuestion";
import { useFieldValidation } from "@/hooks";
import { PageWithHardStopProps } from "@/types/SolarFormProps";
import { roofMaterial } from "./content.json";
import { requireSolarRoute } from "./utils/requireSolarRoute";
import { switchWording } from "./utils/switchWording";
import { TileButtonGroup } from "@origin-digital/ods-tile-button";
import { StyledTileButtonWithImage } from "@/components/StyledTileButton";
import ColorbondTin from "@/assets/colorbond_tin.svg";
import ConcreteTile from "@/assets/concrete_tile.svg";
import TerracottaTile from "@/assets/terracotta_tile.svg";
import KlipLok from "@/assets/klip_lok.svg";
import Slate from "@/assets/slate.svg";
import Asphalt from "@/assets/asphalt.svg";
import Shingle from "@/assets/shingle.svg";
import Fibro from "@/assets/fibro.svg";
import Asbestos from "@/assets/asbestos.svg";
import Timber from "@/assets/timber.svg";
import EpdmRubber from "@/assets/epdm_rubber.svg";
import Decramastic from "@/assets/decramastic.svg";
import FlatConcrete from "@/assets/flat_concrete.svg";
import NotSure from "@/assets/not_sure.svg";
import { Alert, Box, Text } from "@origin-digital/ods-core";

type RoofMaterialProps = PageWithHardStopProps & {
  backExistingSolarRoute: () => Promise<string>;
  degradedNextRoute: () => Promise<string>;
  degradedBackRoute: () => Promise<string>;
};

export const RoofMaterial = memo(
  ({
    backRoute,
    nextRoute,
    hardStopRoute,
    degradedNextRoute,
    degradedBackRoute,
    backExistingSolarRoute,
  }: RoofMaterialProps) => {
    const { getValues, setValue } = useFormContext();
    const { isValid } = useFieldValidation({ fields: ["roofMaterial"] });
    const [selectedRoofMaterial, setSelectedRoofMaterial] = useState(
      getValues("roofMaterial")
    );
    const [error, setError] = useState("");

    const customerType = getValues("customerType");
    const useBuildingWord = true;
    const hardStopMaterialList = ["asbestos", "decramastic"];

    const onBackRoute = () => {
      if (requireSolarRoute(getValues("accurassiHasSolarPanel"))) {
        return backExistingSolarRoute();
      }

      return getValues("degradedExperience")
        ? degradedBackRoute()
        : backRoute();
    };

    const onNextRoute = () => {
      if (hardStopMaterialList.includes(getValues("roofMaterial"))) {
        setValue("hardStop", "roofMaterial");
        return hardStopRoute();
      }

      if (
        getValues("degradedExperience") &&
        !getValues("isKipAndGoInstaller")
      ) {
        return degradedNextRoute();
      }

      return nextRoute();
    };

    const doNext = async () => {
      if (!selectedRoofMaterial) {
        setError(roofMaterial.errorMessage);
      }
      return await isValid();
    };

    const roofImages = [
      <ColorbondTin key={"colorbondTin"} />,
      <ConcreteTile key={"concreteTile"} />,
      <TerracottaTile key={"terracottaTile"} />,
      <KlipLok key={"klipLok"} />,
      <Slate key={"slate"} />,
      <Asphalt key={"asphalt"} />,
      <Shingle key={"shingle"} />,
      <Fibro key={"fibro"} />,
      <Asbestos key={"asbestos"} />,
      <Timber key={"timber"} />,
      <EpdmRubber key={"epdmRubber"} />,
      <Decramastic key={"decramastic"} />,
      <FlatConcrete key={"flatConcrete"} />,
    ];

    const options = roofMaterial.option;
    const roofTypesLength = Object.keys(options).length - 1; // exclude the 'not-sure'

    return (
      <StyledQuestion>
        <div>
          <StyledHeading>
            {switchWording(roofMaterial.heading, customerType, useBuildingWord)}
          </StyledHeading>
          <StyledTextRoofPage>{roofMaterial.description}</StyledTextRoofPage>
          {error && (
            <Box marginBottom={"small"}>
              <Alert tone="critical">
                <Text>{roofMaterial.errorMessage}</Text>
              </Alert>
            </Box>
          )}
          <TileButtonGroup
            cols={{ xs: 2, sm: 3 }}
            id={roofMaterial.id}
            onChange={(_, val) => {
              setSelectedRoofMaterial(val);
              setValue("roofMaterial", val);
              setError("");
            }}
            noChildPadding
          >
            {Object.keys(options).map((key, index) => (
              <StyledTileButtonWithImage
                label={options[key].text}
                togglePosition="bottom"
                key={options[key].value}
                id={options[key].value}
                value={options[key].value}
                selected={options[key].value === selectedRoofMaterial}
                noChildPadding
              >
                {index === roofTypesLength ? (
                  <div id="not-sure">
                    <NotSure key={"notSure"} />
                  </div>
                ) : (
                  <>{roofImages[index]}</>
                )}
              </StyledTileButtonWithImage>
            ))}
          </TileButtonGroup>
        </div>
        <RouterActions
          onBackRoute={onBackRoute}
          onNextRoute={onNextRoute}
          onNext={doNext}
        />
      </StyledQuestion>
    );
  }
);
